/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Info } from "lucide-react";
import Input from "components/Input";
import Button from "components/Button";
import leavePolicyManager from "services/LeavePolicy/leavePolicyManager";
// NOTE: shared resolver so a string `response.data` and an object
// `response.data` both end up as a readable toast message.
import { getApiErrorMessage } from "util/errorMessage";
import {
  LeavePolicy,
  MONTHS_PER_YEAR,
  getMonthlyAccrual,
} from "services/LeavePolicy/leavePolicyInterface";

// Numbers arrive from inputs as strings; coerce "" → undefined so the "required"
// message shows instead of a confusing type error.
const toNumber = (value: unknown, original: unknown) =>
  original === "" || original === null || original === undefined ? undefined : value;

const validationSchema = yup.object({
  leaveType: yup.string().trim().required("Leave type is required"),
  daysPerYear: yup
    .number()
    .transform(toNumber)
    .typeError("Days must be a number")
    .required("Days is required")
    .moreThan(0, "Days must be greater than 0")
    .max(366, "Days can't exceed 366"),
  isAccrual: yup.boolean().default(false),
  // UI-only flag that gates the carry-forward field. NOT part of the payload —
  // see onSubmit, which sends only the numeric maxCarryForward.
  maxCarryForwardEnabled: yup.boolean().default(false),
  maxCarryForward: yup
    .number()
    .transform(toNumber)
    // Only validated when the carry-forward checkbox is on; then the value must
    // be greater than 0 (0 is rejected). When off it is ignored (sent as 0).
    .when("maxCarryForwardEnabled", {
      is: true,
      then: (schema) =>
        schema
          .typeError("Max carry forward must be a number")
          .required("Max carry forward is required")
          .moreThan(0, "Max carry forward must be greater than 0")
          .max(366, "Max carry forward can't exceed 366")
          // Can't carry forward more than the annual entitlement (Days). Uses a
          // custom test so the message can name the current Days value.
          .test("cf-not-exceed-days", function (value) {
            const days = Number(this.parent?.daysPerYear);
            // Skip while Days is empty/invalid — its own rule handles that.
            if (!Number.isFinite(days) || days <= 0) return true;
            if (value == null || Number(value) <= days) return true;
            return this.createError({
              message: `Carry forward can't exceed Days (${days})`,
            });
          }),
      otherwise: (schema) => schema.notRequired(),
    }),
});

interface LeavePolicyFormProps {
  // Present when editing; omit/undefined for a new policy.
  initialValue?: LeavePolicy | null;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  onClose: () => void;
}

// Label row that mirrors the shared Input's label markup, so a checkbox cell
// aligns pixel-for-pixel with the Input fields beside it.
const FieldLabel: React.FC<{ htmlFor?: string; required?: boolean; children: React.ReactNode }> = ({
  htmlFor,
  required,
  children,
}) => (
  <div className="flex items-start justify-between gap-2 mb-2">
    <label
      htmlFor={htmlFor}
      className="block min-w-0 text-sm font-medium text-gray-900 dark:text-white leading-snug break-words"
    >
      {children} {required && <span className="text-red-500">*</span>}
    </label>
  </div>
);

// Theme-aware checkbox in a bordered box matched to the Input field height so
// rows stay aligned. Replaces the previous on/off toggle per design.
const CheckboxBox: React.FC<{
  id: string;
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}> = ({ id, label, checked, disabled, onChange }) => (
  <div
    className={`flex items-center gap-2.5 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2.5 dark:border-gray-600 dark:bg-gray-700 ${disabled ? "opacity-60" : ""
      }`}
  >
    <input
      id={id}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      className="h-4 w-4 shrink-0 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed dark:border-gray-500 dark:bg-gray-600 dark:focus:ring-blue-600"
    />
    <label
      htmlFor={id}
      className={`text-sm text-gray-700 dark:text-gray-200 select-none ${disabled ? "cursor-not-allowed" : "cursor-pointer"
        }`}
    >
      {label}
    </label>
  </div>
);

const LeavePolicyForm: React.FC<LeavePolicyFormProps> = ({
  initialValue,
  onSuccess,
  onError,
  onClose,
}) => {
  const isEdit = Boolean(initialValue?.id);
  const [submitting, setSubmitting] = useState(false);

  // Existing policies with a carry-forward > 0 open with the checkbox on and the
  // field pre-filled; everything else (including add) starts off/empty.
  const initialMaxCarryForward = Number(initialValue?.maxCarryForward) || 0;

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    defaultValues: {
      leaveType: initialValue?.leaveType ?? "",
      daysPerYear: (initialValue?.daysPerYear as any) ?? "",
      isAccrual: initialValue?.isAccrual ?? false,
      maxCarryForwardEnabled: initialMaxCarryForward > 0,
      maxCarryForward: initialMaxCarryForward > 0 ? (initialMaxCarryForward as any) : "",
    },
  });

  const { watch, setValue, formState } = methods;
  const isAccrual = watch("isAccrual");
  const daysPerYear = watch("daysPerYear");
  const carryForwardEnabled = watch("maxCarryForwardEnabled");

  // Live accrual figure — annual entitlement spread across 12 months.
  const { perMonthLabel } = useMemo(() => getMonthlyAccrual(daysPerYear), [daysPerYear]);
  const daysPerYearNum = Number(daysPerYear) || 0;
  const hasDays = daysPerYearNum > 0;

  // Accrual is only meaningful once Days is entered; if Days is cleared, switch
  // accrual back off so a disabled checkbox never stays checked.
  useEffect(() => {
    if (!hasDays && isAccrual) {
      setValue("isAccrual", false, { shouldDirty: true });
    }
  }, [hasDays, isAccrual, setValue]);

  // Carry-forward's limit depends on Days, so re-validate it whenever Days
  // changes — otherwise the "can't exceed Days" error only refreshes when the
  // carry-forward field itself is edited.
  useEffect(() => {
    if (carryForwardEnabled) {
      methods.trigger("maxCarryForward");
    }
  }, [daysPerYear, carryForwardEnabled, methods]);

  // Note: blocks typing a negative value (minus) and scientific "e"/"+" notation
  // on number inputs, complementing the schema's numeric rules.
  const blockNegativeKeys = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (["-", "+", "e", "E"].includes(e.key)) {
      e.preventDefault();
    }
  };

  // Toggling the carry-forward checkbox: clear the number when turning it off so
  // no stale/invalid value lingers (it is sent as 0 while disabled).
  const handleToggleCarryForward = (checked: boolean) => {
    setValue("maxCarryForwardEnabled", checked, { shouldDirty: true, shouldValidate: true });
    if (!checked) {
      setValue("maxCarryForward", "" as any, { shouldDirty: true, shouldValidate: true });
    }
  };

  const onSubmit = async (data: any) => {
    if (submitting) return;
    setSubmitting(true);
    const payload = {
      leaveType: String(data.leaveType).trim(),
      daysPerYear: Number(data.daysPerYear),
      isAccrual: Boolean(data.isAccrual),
      // Checkbox is UI-only and never sent — only the numeric value goes out.
      // When carry-forward is off, send 0.
      maxCarryForward: data.maxCarryForwardEnabled ? Number(data.maxCarryForward) : 0,
    };
    try {
      if (isEdit && initialValue?.id) {
        await leavePolicyManager.update(initialValue.id, payload);
        onSuccess("Leave policy updated successfully");
      } else {
        await leavePolicyManager.create(payload);
        onSuccess("Leave policy added successfully");
      }
    } catch (err: any) {
      console.log("LeavePolicyForm onSubmit error:", err);
      // CHANGE: `err?.response?.data` was evaluated FIRST, so a JSON error body
      // was handed to onError(string) as a raw object — React refuses to render
      // that. The shared resolver type-checks the payload before it reaches the
      // toast. (The clauses after it were also dead: if `data` is falsy,
      // `data.message` cannot be truthy.)
      onError(getApiErrorMessage(err, "Something went wrong"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} className="px-4 pt-2 pb-3">
        {/* Row 1: Leave Type | Days (compact) | Accrual checkbox.
            Note: flex (not a responsive grid) so the three fields stay on ONE
            row based on the fixed-width modal, independent of the viewport —
            a viewport-gated `sm:` grid collapses to a stack when DevTools is
            docked or the window is narrow. min-w-0 lets fields shrink instead
            of wrapping. */}
        <div className="flex items-start gap-x-4 gap-y-1">
          {/* Leave type — free text so admins can name any policy; grows to fill */}
          <div className="flex-1 min-w-0">
            <Input
              name="leaveType"
              label="Leave Type"
              placeholder="e.g. Maternity and paternity leave"
              required
            />
          </div>

          {/* Days — compact fixed width */}
          <div className="w-24 shrink-0">
            <Input
              name="daysPerYear"
              type="number"
              label="Days"
              placeholder="e.g. 12"
              required
              min={0}
              max={366}
              onKeyDown={blockNegativeKeys}
            />
          </div>

          {/* Accrual — checkbox, disabled until Days is entered */}
          <div className="w-44 shrink-0">
            <FieldLabel htmlFor="isAccrual">Accrual</FieldLabel>
            <CheckboxBox
              id="isAccrual"
              label={isAccrual ? "Accrues monthly" : "Granted upfront"}
              checked={Boolean(isAccrual)}
              disabled={!hasDays}
              onChange={(next) => setValue("isAccrual", next, { shouldDirty: true })}
            />
            <div className="min-h-[1.5rem] mt-1">
              {!hasDays && (
                <p className="text-xs font-medium leading-tight text-gray-400 dark:text-gray-500">
                  Enter days to enable
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Row 2: Max Carry Forward checkbox | number field (enabled when checked) */}
        <div className="flex items-start gap-x-4 gap-y-1">
          {/* Carry-forward enable checkbox */}
          <div className="w-56 shrink-0">
            <FieldLabel htmlFor="maxCarryForwardEnabled">Max Carry Forward</FieldLabel>
            <CheckboxBox
              id="maxCarryForwardEnabled"
              label={carryForwardEnabled ? "Carry forward allowed" : "No carry forward"}
              checked={Boolean(carryForwardEnabled)}
              onChange={handleToggleCarryForward}
            />
            <div className="min-h-[1.5rem] mt-1" />
          </div>

          {/* Carry-forward days — only enabled when the checkbox is on; grows to fill */}
          <div className="flex-1 min-w-0">
            <Input
              name="maxCarryForward"
              type="number"
              label="Carry Forward Days"
              placeholder="e.g. 3"
              required={Boolean(carryForwardEnabled)}
              disabled={!carryForwardEnabled}
              min={1}
              max={366}
              onKeyDown={blockNegativeKeys}
            />
          </div>
        </div>

        {/* Always-on informational note. A fixed height (tall enough for the
            longest state) keeps the form/modal from jumping as the copy changes
            between accrual on/off. */}
        <div className="mb-3 flex h-[6.5rem] items-start gap-2.5 overflow-hidden rounded-lg border border-blue-200 bg-blue-50 p-3 text-blue-800 dark:border-blue-800/60 dark:bg-blue-900/30 dark:text-blue-200">
          <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <div className="text-sm leading-relaxed">
            <p className="font-semibold">
              {isAccrual ? "Accrual enabled" : "About monthly accrual"}
            </p>

            {isAccrual ? (
              hasDays ? (
                <p>
                  Employees earn{" "}
                  <span className="font-semibold tabular-nums">{perMonthLabel}</span> day
                  {perMonthLabel === "1.00" ? "" : "s"} per month{" "}
                  <span className="text-blue-700/80 dark:text-blue-300/80">
                    ({daysPerYearNum} ÷ {MONTHS_PER_YEAR} months)
                  </span>
                  . Balance builds up monthly, not upfront.
                </p>
              ) : (
                <p>
                  Enter <span className="font-semibold">Days Per Year</span> to see the monthly
                  accrual (<span className="font-mono">days per year ÷ {MONTHS_PER_YEAR}</span>).
                </p>
              )
            ) : (
              <p>
                When enabled, the entitlement is earned gradually —{" "}
                <span className="font-mono">days per year ÷ {MONTHS_PER_YEAR}</span> each month
                {hasDays && (
                  <>
                    {" "}(
                    <span className="font-semibold tabular-nums">{perMonthLabel}</span>/month)
                  </>
                )}
                . Otherwise granted upfront.
              </p>
            )}
          </div>
        </div>

        <div className="mt-3 flex justify-end gap-2">
          <Button
            onClick={onClose}
            type="button"
            className="!bg-slate-100 hover:!bg-slate-200 !text-slate-700 dark:!bg-slate-700 dark:hover:!bg-slate-600 dark:!text-slate-100"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            loading={submitting}
            disabled={submitting || !formState.isValid}
            className="!text-white !bg-blue-700 hover:!bg-blue-800 focus:!ring-4 focus:!outline-none focus:!ring-blue-300 dark:!bg-blue-600 dark:hover:!bg-blue-700 dark:focus:!ring-blue-800"
          >
            {isEdit ? "Update Policy" : "Add Policy"}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default LeavePolicyForm;
